import React from 'react';
import SEO from '../../components/SEO/SEO';

import { Link } from 'react-router-dom';
import './WebDevMain.css';

const WebDevMain = () => {
  const webServices = [
    {
      id: 'custom',
      title: 'Custom Websites',
      subtitle: 'React, Next.js & Modern Frameworks',
      description: 'Build blazing-fast, scalable web applications with custom code tailored to your exact business requirements.',
      icon: '💻',
      features: [
        'React & Next.js Development',
        'Headless CMS Integration',
        'API Development & Integration',
        'Performance Optimization & SEO'
      ],
      link: '/services/web-development/custom'
    },
    {
      id: 'wordpress',
      title: 'WordPress Development',
      subtitle: 'Custom Themes & Plugins',
      description: 'Lightweight, SEO-optimized WordPress sites with custom themes, plugins, and easy content management.',
      icon: '📝',
      features: [
        'Custom Theme Development',
        'Plugin Optimization & Development',
        'WooCommerce E-Commerce Setup',
        'Speed & Security Hardening'
      ],
      link: '/services/web-development/wordpress'
    },
    {
      id: 'shopify',
      title: 'Shopify Development',
      subtitle: 'E-Commerce Solutions',
      description: 'High-converting Shopify stores with custom themes, seamless checkout, and powerful e-commerce features.',
      icon: '🛒',
      features: [
        'Custom Shopify Theme Development',
        'Shopify App Integration',
        'Shopify Plus for Enterprise',
        'Migration & Data Transfer'
      ],
      link: '/services/web-development/shopify'
    },
    {
      id: 'webflow',
      title: 'Webflow Design',
      subtitle: 'Visual Development Platform',
      description: 'Stunning, animation-rich websites built on Webflow with no-code updates and enterprise-level performance.',
      icon: '🎨',
      features: [
        'Custom Webflow Site Design',
        'Animation & Interaction Design',
        'CMS Setup & Client Training',
        'Responsive & Mobile-First'
      ],
      link: '/services/web-development/webflow'
    }
  ];

  return (
    <div className="webdev-page">
            <SEO 
                title="Web Dev Main Services | Digi Network" 
                description="Learn more about our Web Dev Main services to grow your business online with Digi Network." 
                canonical="/webdevelopment/webdevmain" 
            />

      <section className="webdev-hero">
        <div className="webdev-hero-bg"></div>
        <div className="container">
          <div className="webdev-hero-content">
            <div className="label">WEB DEVELOPMENT</div>
            <h1>Fast, Scalable Websites That Convert</h1>
            <p className="webdev-hero-description">
              From custom-coded applications to WordPress, Shopify, and Webflow, we build websites that combine
              stunning design with lightning-fast performance. Every site we create is optimized for
              conversions, SEO, and user experience.
            </p>
            <div className="webdev-hero-cta">
              <Link to="/contact" className="btn btn-primary">
                Start Your Project
                <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                  <path d="M7.5 15L12.5 10L7.5 5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </Link>
              <Link to="/portfolio" className="btn btn-outline">View Portfolio</Link>
            </div>
          </div>
        </div>
      </section>

      <section className="webdev-stats">
        <div className="container">
          <div className="stats-grid">
            <div className="stat-card">
              <div className="stat-number">200+</div>
              <div className="stat-label">Websites Launched</div>
            </div>
            <div className="stat-card">
              <div className="stat-number">95+</div>
              <div className="stat-label">PageSpeed Scores</div>
            </div>
            <div className="stat-card">
              <div className="stat-number">3x</div>
              <div className="stat-label">Average Conversion Lift</div>
            </div>
            <div className="stat-card">
              <div className="stat-number">24/7</div>
              <div className="stat-label">Support & Monitoring</div>
            </div>
          </div>
        </div>
      </section>

      <section className="web-services-grid-section">
        <div className="container">
          <div className="section-header">
            <div className="label">DEVELOPMENT SOLUTIONS</div>
            <h2>Choose Your Platform</h2>
            <p>We specialize in multiple platforms to match your needs, budget, and technical requirements.</p>
          </div>

          <div className="web-services-grid">
            {webServices.map((service) => (
              <div key={service.id} className="web-service-card">
                <div className="web-service-icon">{service.icon}</div>
                <h3>{service.title}</h3>
                <div className="web-service-subtitle">{service.subtitle}</div>
                <p className="web-service-description">{service.description}</p>

                <ul className="web-service-features">
                  {service.features.map((feature, index) => (
                    <li key={index}>
                      <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                        <circle cx="10" cy="10" r="10" fill="#0037FC" opacity="0.1"/>
                        <path d="M6 10L9 13L14 7" stroke="#0037FC" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                      {feature}
                    </li>
                  ))}
                </ul>

                <Link to={service.link} className="btn btn-dark">
                  Learn More
                  <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                    <path d="M7.5 15L12.5 10L7.5 5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </Link>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="webdev-process">
        <div className="container">
          <div className="section-header">
            <div className="label">OUR PROCESS</div>
            <h2>From Concept to Launch</h2>
          </div>

          <div className="process-grid">
            <div className="process-step">
              <div className="process-number">01</div>
              <h3>Discovery & Planning</h3>
              <p>Define goals, target audience, site architecture, and technical requirements.</p>
            </div>

            <div className="process-step">
              <div className="process-number">02</div>
              <h3>Design & Prototype</h3>
              <p>Create wireframes and high-fidelity designs that align with your brand identity.</p>
            </div>

            <div className="process-step">
              <div className="process-number">03</div>
              <h3>Development & Testing</h3>
              <p>Build your site with clean code, optimize performance, and ensure cross-browser compatibility.</p>
            </div>

            <div className="process-step">
              <div className="process-number">04</div>
              <h3>Launch & Support</h3>
              <p>Deploy to production, monitor performance, and provide ongoing maintenance and updates.</p>
            </div>
          </div>
        </div>
      </section>

      <section className="webdev-tech-stack">
        <div className="container">
          <div className="section-header">
            <div className="label">TECHNOLOGIES</div>
            <h2>Modern Tech Stack</h2>
            <p>We use industry-leading tools and frameworks to build future-proof websites.</p>
          </div>

          <div className="tech-categories">
            <div className="tech-category">
              <h3>Frontend</h3>
              <div className="tech-tags">
                <span>React</span>
                <span>Next.js</span>
                <span>TypeScript</span>
                <span>Tailwind CSS</span>
              </div>
            </div>

            <div className="tech-category">
              <h3>CMS & E-Commerce Platforms</h3>
              <div className="tech-tags">
                <span>WordPress</span>
                <span>Shopify</span>
                <span>Webflow</span>
              </div>
            </div>

            <div className="tech-category">
              <h3>Performance</h3>
              <div className="tech-tags">
                <span>CDN Integration</span>
                <span>Image Optimization</span>
                <span>Code Splitting</span>
                <span>Lazy Loading</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="webdev-cta">
        <div className="container">
          <div className="webdev-cta-content">
            <h2>Ready to Build Your Website?</h2>
            <p>Let's create a fast, beautiful website that drives results for your business.</p>
            <Link to="/contact" className="btn btn-primary">
              Get a Free Consultation
              <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                <path d="M7.5 15L12.5 10L7.5 5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default WebDevMain;
